// ===================================
// ACTIONS TABLE COMPONENT - src/components/ActionsTable.tsx
// ===================================
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import ActionsApi from '../services/actionsApi';
import { parseUserPermissions } from '../utils/permissions';
import { Action, Topic, PaginatedResponse, ListActionsParams } from '../types';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  ChevronDown,
  MoreHorizontal
} from 'lucide-react';
import Button from './ui/button';
import Badge from './ui/badge';

interface ActionsTableProps {
  topic: Topic;
  refreshTrigger?: number;
  onActionSelect?: (action: Action) => void;
  onActionEdit?: (action: Action) => void;
  onActionCreate?: () => void;
  onActionDelete?: (action: Action) => void;  // Changed: now accepts Action object instead of (topicId, actionId)
}

const ActionsTable: React.FC<ActionsTableProps> = ({
  topic,
  refreshTrigger = 0,
  onActionSelect,
  onActionEdit,
  onActionCreate,
  onActionDelete
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const permissions = parseUserPermissions(user);

  // Table state
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Filters and pagination
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sortBy, setSortBy] = useState<'id' | 'action_name' | 'created_at' | 'updated_at'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Load actions
  const loadActions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListActionsParams = {
        topic_id: topic.id,
        q: search || undefined,
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      const response: PaginatedResponse<Action> = await ActionsApi.getTopicActions(params);
      
      setActions(response.items);
      setTotal(response.total);

    } catch (err) {
      const error = ActionsApi.handleApiError(err);
      setError(error.message);
      console.error('Failed to load actions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load actions on mount and when dependencies change
  useEffect(() => {
    loadActions();
  }, [topic.id, search, page, sortBy, sortOrder, refreshTrigger]);

  // Handle search with debounce
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      setPage(1);
    }, 300);
    
    setSearchTimeout(timeout);
  };

  // Handle sort change
  const handleSort = (newSortBy: typeof sortBy) => {
    if (newSortBy === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // Flow status badge
  const getFlowStatusBadge = (action: Action) => {
    const status = action.flow_status || 'unknown';
    
    switch (status) {
      case 'valid':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Valid
          </Badge>
        );
      case 'invalid':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            Invalid
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Unknown
          </Badge>
        );
    }
  };

  // Validate flow for specific action
  const validateFlow = async (action: Action) => {
    try {
      const result = await ActionsApi.validateFlow({ flow_name: action.flow_name });
      
      addNotification({
        type: result.exists ? 'success' : 'error',
        title: 'Flow Validation',
        message: result.exists 
          ? `Flow "${action.flow_name}" exists in Salesforce`
          : `Flow "${action.flow_name}" not found in Salesforce`
      });

      loadActions();

    } catch (err) {
      const error = ActionsApi.handleApiError(err);
      addNotification({
        type: 'error',
        title: 'Flow validation failed',
        message: error.message
      });
    }
  };

  // Pagination info
  const totalPages = Math.ceil(total / limit);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="space-y-4 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            Actions for {topic.name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {total} action{total !== 1 ? 's' : ''} total
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadActions}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          {permissions.canCreate && (
            <Button 
              size="sm"
              onClick={onActionCreate}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Action
            </Button>
          )}
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search actions by name, description, or flow..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Filter className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600 whitespace-nowrap">Sort by:</span>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [newSortBy, newSortOrder] = e.target.value.split('-');
              setSortBy(newSortBy as typeof sortBy);
              setSortOrder(newSortOrder as typeof sortOrder);
              setPage(1);
            }}
            className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="updated_at-desc">Recently Updated</option>
            <option value="created_at-desc">Recently Created</option>
            <option value="action_name-asc">Name (A-Z)</option>
            <option value="action_name-desc">Name (Z-A)</option>
          </select>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadActions}
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading && actions.length === 0 && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions table - Desktop */}
      {!loading && actions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="hidden lg:block">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-1/5"
                      onClick={() => handleSort('action_name')}
                    >
                      <div className="flex items-center gap-1">
                        Action Name
                        <ChevronDown className="h-3 w-3" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Flow Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Fields
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Flow Status
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-1/8"
                      onClick={() => handleSort('updated_at')}
                    >
                      <div className="flex items-center gap-1">
                        Updated
                        <ChevronDown className="h-3 w-3" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {actions.map((action) => (
                    <tr key={action.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {action.action_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {action.id}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 truncate max-w-xs" title={action.description}>
                          {action.description || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <code className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded truncate block max-w-32" title={action.flow_name}>
                          {action.flow_name}
                        </code>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <span>{action.input_count}</span>
                          <span className="text-gray-400">in</span>
                          <span>•</span>
                          <span>{action.output_count}</span>
                          <span className="text-gray-400">out</span>
                          <span>•</span>
                          <span className="font-medium text-red-600">{action.required_count}</span>
                          <span className="text-red-400">req</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getFlowStatusBadge(action)}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => validateFlow(action)}
                            className="h-6 w-6 p-0"
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(action.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onActionSelect?.(action)}
                            className="h-8 w-8 p-0"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          
                          {permissions.canUpdate && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onActionEdit?.(action)}
                              className="h-8 w-8 p-0"
                              title="Edit Action"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {permissions.canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onActionDelete?.(action)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete Action"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile/Tablet card layout */}
          <div className="lg:hidden divide-y divide-gray-200">
            {actions.map((action) => (
              <div key={action.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {action.action_name}
                      </h3>
                      {getFlowStatusBadge(action)}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {action.description || 'No description'}
                    </p>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                      <span>ID: {action.id}</span>
                      <span>{action.input_count} in • {action.output_count} out • {action.required_count} req</span>
                    </div>
                    
                    <code className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {action.flow_name}
                    </code>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => validateFlow(action)}
                      className="h-8 w-8 p-0"
                      title="Validate Flow"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onActionSelect?.(action)}
                      className="h-8 w-8 p-0"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    
                    {permissions.canUpdate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onActionEdit?.(action)}
                        className="h-8 w-8 p-0"
                        title="Edit Action"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {permissions.canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onActionDelete?.(action)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete Action"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {startItem} to {endItem} of {total} results
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    Page {page} of {totalPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && actions.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No actions found</h3>
          <p className="text-gray-600 mb-4">
            {search 
              ? `No actions match "${search}"`
              : `Topic "${topic.name}" doesn't have any actions yet.`
            }
          </p>
          {permissions.canCreate && !search && (
            <Button onClick={onActionCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Action
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionsTable;